import { useEffect, useState, Suspense, lazy } from 'react';
import { Box, Card, CardContent, Grid, lighten, SxProps, Theme, Typography, TypographyVariant } from '@mui/material';
import { ContentState, EditorState, convertFromRaw } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { sanitizeHtml, trySanitizeHtml } from '@utils/sanitizeHtml';
import { TextClickable, ITextClickable } from '@components/container/TextClickable';
import { CardAttachmentFile } from '@components/noteMessage/CardAttachmentFile';
import { Note, NoteType } from '@models/note';
import { CardHeaderNote } from '@components/noteMessage/CardHeaderNote';
import { ContentFooter } from '@components/noteMessage/ContentFooter';
import { getMediaType } from '@utils/fileUtils';
import { TypographyHighlighter } from '@components/typography/TypographyHighlighter';
import { containerEditor } from '@components/richTextEditor/RichTextEditor.styles';

const LazyRichTextEditor = lazy(() =>
  import('@components/richTextEditor/RichTextEditor').then((m) => ({ default: m.RichTextEditor })),
);

const hoverBgStyle = {
  '&.MuiPaper-elevation2': {
    '&.MuiCard-root': {
      '&:hover': {
        backgroundColor: 'background.main',
      },
    },
  },
};

const elevation: Record<string, number> = {
  NOTE: 1,
  MESSAGE: 2,
};

export interface ICardNote {
  textClickable?: ITextClickable;
  hasPinnedNote?: boolean;
  searchBarText?: string;
}

export function CardNote(props: Note & ICardNote) {
  const { body, type, artifacts, textClickable, hasPinnedNote, searchBarText, deletedAt } = props;

  const [onHover, setOnHover] = useState({ active: false, disabled: false });
  const [editingNote, setEditingNote] = useState(false);

  const handleOnHover = () => {
    setOnHover((prevState) => ({
      ...prevState,
      active: true,
    }));
  };
  const handleOutHover = () => {
    setOnHover((prevState) => ({
      ...prevState,
      active: false,
    }));
  };
  const handleActiveHover = () => {
    setOnHover((prevState) => ({
      ...prevState,
      disabled: false,
    }));
  };
  const handleDisabledHover = () => {
    setOnHover((prevState) => ({
      ...prevState,
      disabled: true,
    }));
  };

  useEffect(() => {
    if (onHover.disabled) {
      handleOutHover();
    }
  }, [onHover.disabled]);

  if (deletedAt) {
    return null;
  }

  return (
    <Card
      elevation={elevation[type]}
      id='card-wrapper'
      sx={{
        ...(artifacts?.length && onHover.active ? hoverBgStyle : undefined),
        backgroundColor: 'drawerNavigation.main',
      }}
      onMouseOver={onHover.disabled ? undefined : handleOnHover}
      onMouseLeave={onHover.disabled ? undefined : handleOutHover}
    >
      <CardHeaderNote
        {...props}
        searchBarText={searchBarText}
        hasTextClickable={textClickable !== undefined}
        hasPinnedNote={hasPinnedNote}
        editingNote={editingNote}
        onEditNote={() => setEditingNote(true)}
      />
      <CardContent>
        {editingNote ? (
          <Box
            sx={containerEditor}
            style={{
              padding: '0px',
            }}
          >
            <Suspense fallback={null}>
              <LazyRichTextEditor
                type={NoteType.Note}
                config={{
                  editorWidth: '240px',
                }}
                editConfig={{
                  body,
                  editing: true,
                  editingAttachments: artifacts,
                  onCancelEdit: () => setEditingNote(false),
                  isPinned: props.isPinned,
                  noteId: props.id,
                  quoteId: props.quoteId,
                  customerId: props.customerId,
                  brokerageId: props.brokerageId,
                  policyId: props.policyId,
                }}
              />
            </Suspense>
          </Box>
        ) : (
          <>
            {textClickable ? (
              <TextClickable
                variant={type === NoteType.Note ? ('bodySmall' as TypographyVariant) : 'caption'}
                text={textClickable.text}
                buttonText={textClickable.buttonText}
                onClick={textClickable.onClick}
              />
            ) : null}
            {body !== undefined ? <HtmlContent body={body} searchBarText={searchBarText} /> : undefined}
            {artifacts?.length ? (
              <Grid
                container
                rowSpacing='4px'
                sx={{
                  flexDirection: 'column',
                  marginTop: body ? '12px' : '4px',
                  width: '100%',
                  borderTop: '1px solid gray',
                  borderColor: 'border.main',
                  mt: 2,
                  pt: 1,
                  gap: 0.25,
                }}
              >
                <Typography variant='caption' color='text.subdued'>
                  Attachments
                </Typography>
                {artifacts.map((artifact) => (
                  <Grid size={{ xs: 12 }} key={artifact.id}>
                    <CardAttachmentFile
                      {...artifact}
                      variant='dense'
                      allowViewInModal
                      type={getMediaType(artifact.type)}
                      searchBarText={searchBarText}
                      isNote={type === NoteType.Note}
                      state='sent'
                      onDisabledHoverParent={handleDisabledHover}
                      onActiveHoverParent={handleActiveHover}
                      menuItems={[]}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : null}
          </>
        )}
        <ContentFooter {...props} />
      </CardContent>
    </Card>
  );
}

export function HtmlContent({
  body,
  searchBarText,
  sxProps,
}: {
  body: string;
  searchBarText?: string;
  sxProps?: SxProps;
}) {
  let state;
  try {
    const parsedBody = JSON.parse(body);
    state = convertFromRaw(parsedBody);
  } catch (e) {
    state = ContentState.createFromText(body);
  }
  const editorValue = EditorState.createWithContent(state);
  const stringHtml = stateToHTML(editorValue.getCurrentContent());

  // Attempt synchronous sanitize if already prefetched, otherwise fallback to original html then async update
  const [sanitized, setSanitized] = useState(() => trySanitizeHtml(stringHtml) ?? stringHtml);
  useEffect(() => {
    let mounted = true;
    sanitizeHtml(stringHtml).then((res) => {
      if (mounted) setSanitized(res);
    });
    return () => {
      mounted = false;
    };
  }, [stringHtml]);

  return (
    <TypographyHighlighter
      sanitizedHtml={sanitized}
      variant='bodySmall'
      color='common.black'
      highlightText={searchBarText}
      sx={{
        wordBreak: 'break-word',
        color: 'text.main',
        '& a': {
          color: 'secondary.main',
        },
        '& p': {
          margin: '0',
        },
        mark: {
          backgroundColor: (theme: Theme) => lighten(theme.palette.secondary.main, 0.8),
          fontWeight: 1000,
        },
        ...(sxProps ?? {}),
      }}
    />
  );
}
